import { useContext, useState } from "react";
import "../../../styles/panel/user/SiteRequestForm.css";
import { CheckIcon } from "./icons";
import { STEPS, emptyOwner, emptyStore } from "./constants";
import OwnerOtpStep, { isOwnerOtpStepValid } from "./OwnerOtpStep";
import StoreStep, { isStoreStepValid } from "./StoreStep";
import TemplateStep, { isTemplateStepValid } from "./TemplateStep";
import PlanStep, { isPlanStepValid } from "./PlanStep";
import PaymentStep, { isPaymentStepValid, isCardToCard } from "./PaymentStep";
import { submitPendingStoreRequest } from "../../../api/storeRequest";
import { swalError, swalSuccess } from "../../../utils/swalAlert";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";

// ✅ تابع تبدیل اعداد فارسی به انگلیسی
function convertPersianToEnglish(str) {
  if (!str) return str;
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

  return String(str)
    .replace(/[۰-۹]/g, (d) => persianDigits.indexOf(d).toString())
    .replace(/[٠-٩]/g, (d) => arabicDigits.indexOf(d).toString());
}

async function compressImage(file, maxSizeKB = 500) {
  if (file.size / 1024 <= maxSizeKB) return file;
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, Math.sqrt((maxSizeKB * 1024) / file.size));
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: "image/jpeg" })),
        "image/jpeg",
        0.8,
      );
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

// ⚠️ برخلاف SiteRequestForm، مالک اینجا کاربر لاگین‌شده نیست
// و از طریق موبایل + OTP احراز می‌شود (به OwnerOtpStep نگاه کن)
export default function RegisterSiteForm() {
  const { user } = useContext(AuthContext);
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [owner, setOwner] = useState({
    ...emptyOwner,
    verified: false,
    owner_id: null,
  });
  const [store, setStore] = useState(emptyStore);
  const [template, setTemplate] = useState(null);
  const [plan, setPlan] = useState(null);
  const [payMethod, setPayMethod] = useState(null);
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [cardTrackingCode, setCardTrackingCode] = useState("");
  const [showCardModal, setShowCardModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [discount, setDiscount] = useState(null); // { code, percent, amount } | null

  const navigate = useNavigate();
  const goTo = (i) => {
    if (i < step) setStep(i); // فقط برگشت به مراحل قبلی مجاز است
  };

  const isStepValid = () => {
    switch (STEPS[step].id) {
      case "owner":
        return isOwnerOtpStepValid(owner);
      case "store":
        return isStoreStepValid(store);
      case "template":
        return isTemplateStepValid(template);
      case "plan":
        return isPlanStepValid(plan);
      case "payment":
        return isPaymentStepValid(payMethod, agreedToRules);
      default:
        return true;
    }
  };

  const next = () => {
    if (!isStepValid()) return;

    if (
      STEPS[step].id === "payment" &&
      isCardToCard(payMethod) &&
      !cardTrackingCode
    ) {
      setShowCardModal(true);
      return;
    }

    if (step === STEPS.length - 1) {
      return;
    }

    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const prev = () => setStep((s) => Math.max(s - 1, 0));

  // بعد از تایید موفق OTP، خودکار به مرحله «اطلاعات فروشگاه» می‌رویم
  const handleOwnerVerified = () => {
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handlePaymentConfirm = async (method, txNumber) => {
    setShowCardModal(false);
    setPaymentError("");
    setLoading(true);

    try {
      // مالک قبلاً از طریق OTP احراز شده و owner_id از پاسخ سرور verifyOwnerOtp گرفته شده
      if (!owner.owner_id) {
        throw new Error("هویت مالک تایید نشده است");
      }

      const data = new FormData();

      data.append("owner_id", owner.owner_id);
      data.append("owner_mobile", convertPersianToEnglish(owner.mobile));

      data.append("store_name", store.storeName);
      data.append("store_slug", convertPersianToEnglish(store.slug));
      data.append("store_type_id", store.category);
      data.append("province_id", store.province);
      data.append("city_id", store.city);
      data.append("phone", convertPersianToEnglish(store.landline));
      data.append("mobile", convertPersianToEnglish(store.mobile));
      data.append("address", store.address);
      data.append("template_id", template.id);
      data.append("plan_id", plan.id);
      data.append("payment_method", method);

      if (method === "card_to_card") {
        data.append("transaction_number", txNumber);
      }

      if (discount?.code) {
        data.append("discount_code", convertPersianToEnglish(discount.code));
      }

      if (store.logoFile) {
        const compressedLogo = await compressImage(store.logoFile);
        data.append("logo", compressedLogo);
      }

      if (store.imageFile) {
        const compressedStoreImage = await compressImage(store.imageFile);
        data.append("store_image", compressedStoreImage);
      }

      // ✅ ثبت pending request برای مالکی که با OTP احراز شده
      const response = await submitPendingStoreRequest(data);

      // ✅ اگر درگاه → باید payment_url برگرده و هدایت بشیم
      if (method === "gateway") {
        if (response.data?.payment_url) {
          window.location.href = response.data.payment_url;
          return;
        } else {
          throw new Error("URL درگاه دریافت نشد");
        }
      }

      await swalSuccess({
        icon: "success",
        title: "ثبت شد",
        text:
          method === "card_to_card"
            ? "درخواست شما ثبت شد و پس از بررسی واریزی حداکثر تا ۳۰ دقیقه فعال خواهد شد."
            : "درخواست ساخت سایت شما با موفقیت ثبت شد.",
        confirmButtonText: "باشه",
      });
      if (user.role == "user") {
        navigate("/panel/user/my-sites");
      } else {
        navigate("/panel/supporter/manage-shops");
      }
    } catch (err) {
      console.log(err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.transaction_number?.[0] ||
        err?.message ||
        "مشکلی پیش آمد";
      await swalError({
        title: "خطا در ثبت",
        text: err?.message,
      });
      setPaymentError(detail);
    }

    setLoading(false);
  };

  const isOwnerStep = STEPS[step].id === "owner";

  return (
    <div className="request-page">
      <div className="request-header">
        <div>
          <h1>ثبت سایت برای دیگران</h1>
          <p>در ۵ مرحله ساده، فروشگاه اینترنتی مشتری‌ت رو راه‌اندازی کن</p>
        </div>
        <div className="request-header-side">
          <span className="step-count">
            مرحله {step + 1} از {STEPS.length}
          </span>
          <a
            className="request-video-link"
            href="https://www.aparat.com/v/eigv177"
            target="_blank"
            rel="noreferrer"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path d="M10 8.5L15.5 12L10 15.5V8.5Z" fill="currentColor" />
            </svg>
            مشاهده آموزش ویدیویی
          </a>
        </div>
      </div>

      <div className="request-stepper">
        <div className="stepper-rail">
          <div
            className="stepper-rail-fill"
            style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }}
          />
        </div>
        {STEPS.map((s, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <button
              type="button"
              key={s.id}
              className={`step-node${active ? " active" : ""}${
                done ? " done" : ""
              }${i < step ? " clickable" : ""}`}
              onClick={() => goTo(i)}
            >
              <span className="step-node-circle">
                {done ? <CheckIcon /> : i + 1}
              </span>
              <span className="step-node-label">{s.label}</span>
            </button>
          );
        })}
      </div>

      <div className="request-card">
        {STEPS[step].id === "owner" && (
          <OwnerOtpStep
            owner={owner}
            setOwner={setOwner}
            onVerified={handleOwnerVerified}
          />
        )}

        {STEPS[step].id === "store" && (
          <StoreStep store={store} setStore={setStore} />
        )}

        {STEPS[step].id === "template" && (
          <TemplateStep template={template} setTemplate={setTemplate} />
        )}

        {STEPS[step].id === "plan" && (
          <PlanStep plan={plan} setPlan={setPlan} />
        )}
        {STEPS[step].id === "payment" && (
          <PaymentStep
            owner={owner}
            store={store}
            template={template}
            plan={plan}
            payMethod={payMethod}
            setPayMethod={setPayMethod}
            agreedToRules={agreedToRules}
            setAgreedToRules={setAgreedToRules}
            cardTrackingCode={cardTrackingCode}
            setCardTrackingCode={setCardTrackingCode}
            showCardModal={showCardModal}
            setShowCardModal={setShowCardModal}
            onConfirmCardPayment={handlePaymentConfirm}
            onDiscountApplied={setDiscount}
          />
        )}
      </div>

      {!isOwnerStep && paymentError && (
        <div className="otp-error">{paymentError}</div>
      )}

      {/* مرحله مالک، ناوبری خودش رو داره (ارسال/تایید کد) و با تایید OTP خودکار جلو می‌ره */}
      {!isOwnerStep && (
        <div className="request-nav">
          <button
            type="button"
            className="request-btn ghost"
            onClick={prev}
            disabled={step === 0}
          >
            مرحله قبل
          </button>
          <button
            type="button"
            className="request-btn primary"
            onClick={() => {
              if (step === STEPS.length - 1) {
                if (isCardToCard(payMethod)) {
                  setShowCardModal(true);
                } else {
                  handlePaymentConfirm("gateway");
                }
              } else {
                next();
              }
            }}
            disabled={!isStepValid() || loading}
          >
            {step === STEPS.length - 1 ? "پرداخت و ثبت درخواست" : "مرحله بعد"}
          </button>
        </div>
      )}
    </div>
  );
}
